import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { useGetTicketStatusesQuery, useGetTicketTypesQuery, useGetTicketUsersQuery } from '../../../api/tickets.js';
import { useLocation } from 'react-router-dom';
import IconCloseFull from '../../images/IconCloseFull.jsx';

const TicketFilter = ({ onFilterChange, filters, setShowFilter }) => {
    const location = useLocation();
    const isAdminPath = location.pathname === '/tickets';

    const { data: ticketTypes, isLoading: ticketTypesLoading } = useGetTicketTypesQuery();
    const { data: ticketStatuses, isLoading: ticketStatusesLoading } = useGetTicketStatusesQuery();
    const { data: ticketUsers, isLoading: ticketUsersLoading } = useGetTicketUsersQuery(undefined, {
        skip: !isAdminPath
    });

    const ticketTypesList = ticketTypes?.response?.ticket_types || [];
    const ticketStatusesList = ticketStatuses?.response?.ticket_statuses || [];
    const ticketUsersList = ticketUsers?.response?.users || [];

    const [selectedStatus, setSelectedStatus] = useState(filters.status || null);
    const [selectedType, setSelectedType] = useState(filters.type || null);
    const [selectedDate, setSelectedDate] = useState(filters.date || null);
    const [selectedUser, setSelectedUser] = useState(filters.user || null);

    const onStatusChange = (e) => {
        const updatedStatus = e.value;
        setSelectedStatus(updatedStatus);
        onFilterChange({
            ...filters,
            status: updatedStatus || null
        });
    };

    const onTypeChange = (e) => {
        const updatedType = e.value;
        setSelectedType(updatedType);
        onFilterChange({
            ...filters,
            type: updatedType || null
        });
    };

    const onDateChange = (e) => {
        const updatedDate = e.value;
        setSelectedDate(updatedDate);
        onFilterChange({
            ...filters,
            date: updatedDate || null
        });
    };

    const onUserChange = (e) => {
        const updatedUser = e.value;
        setSelectedUser(updatedUser);
        onFilterChange({
            ...filters,
            user: updatedUser || null
        });
    };

    const clearDate = () => {
        setSelectedDate(null);
        onFilterChange({ ...filters, date: null });
    };

    const handleClose = () => {
        setShowFilter(false);
    };

    useEffect(() => {
        setSelectedStatus(filters.status || null);
        setSelectedType(filters.type || null);
        setSelectedDate(filters.date || null);
        setSelectedUser(filters.user || null);
    }, [filters]);

    return (
        <div className='p-4 bg-theme1-pinkGray mb-6 relative'>
            <button type='button' onClick={handleClose} className='absolute -top-1 -right-1'>
                <IconCloseFull className='fill-theme1-primary2' />
            </button>
            {isAdminPath && (
                <div className='mb-4'>
                    <label className='block text-base font-medium mb-2 text-theme1-primary2'>Ticket User</label>
                    <Dropdown
                        value={selectedUser}
                        options={ticketUsersList}
                        onChange={onUserChange}
                        optionLabel='name'
                        optionValue='id'
                        placeholder={ticketUsersLoading ? 'Loading users...' : 'Select User'}
                        className='text-theme1-text1 rounded-2xl border border-theme1-text3 w-full outline-0 focus:outline-0 shadow-none text-base'
                        disabled={ticketUsersLoading}
                        showClear
                    />
                </div>
            )}
            <div className='mb-4'>
                <label className='block text-base font-medium mb-2 text-theme1-primary2'>Ticket Status</label>
                <Dropdown
                    value={selectedStatus}
                    options={ticketStatusesList}
                    onChange={onStatusChange}
                    optionLabel='label'
                    optionValue='value'
                    placeholder={ticketStatusesLoading ? 'Loading tickets statuses...' : 'Select Status'}
                    className='text-theme1-text1 rounded-2xl border border-theme1-text3 w-full outline-0 focus:outline-0 shadow-none text-base'
                    dropdownIcon={
                        <svg width='22' height='22' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M6 12L9 15L12 12' strokeLinecap='round' strokeLinejoin='round' />
                            <path d='M6 6L9 3L12 6' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                    }
                    collapseIcon={
                        <svg width='22' height='22' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M6 12L9 15L12 12' strokeLinecap='round' strokeLinejoin='round' />
                            <path d='M6 6L9 3L12 6' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                    }
                    disabled={ticketStatusesLoading}
                    showClear
                />
            </div>

            <div className='mb-4'>
                <label className='block text-base font-medium mb-2 text-theme1-primary2'>Ticket Type</label>
                <Dropdown
                    value={selectedType}
                    options={ticketTypesList}
                    onChange={onTypeChange}
                    optionLabel='label'
                    optionValue='value'
                    placeholder={ticketTypesLoading ? 'Loading tickets types...' : 'Select Type'}
                    className='text-theme1-text1 rounded-2xl border border-theme1-text3 w-full outline-0 focus:outline-0 shadow-none text-base'
                    dropdownIcon={
                        <svg width='22' height='22' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M6 12L9 15L12 12' strokeLinecap='round' strokeLinejoin='round' />
                            <path d='M6 6L9 3L12 6' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                    }
                    collapseIcon={
                        <svg width='22' height='22' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M6 12L9 15L12 12' strokeLinecap='round' strokeLinejoin='round' />
                            <path d='M6 6L9 3L12 6' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                    }
                    disabled={ticketTypesLoading}
                    showClear
                />
            </div>

            <div className='mb-4'>
                <label className='block text-base font-medium mb-2 text-theme1-primary2'>Ticket Date</label>
                <div className='flex items-center gap-2'>
                    <Calendar
                        value={selectedDate}
                        onChange={onDateChange}
                        placeholder='Select Date'
                        className='w-full text-theme1-text1 rounded-2xl border border-theme1-text3 text-base'
                        inputClassName='custom-calendar-input'
                        dateFormat='dd/mm/yy'
                    />
                    {selectedDate && (
                        <button type='button' onClick={clearDate} className='text-sm text-theme1-primary2'>
                            Clear
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TicketFilter;
